import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, ExternalLink, RefreshCw, ClipboardList,
  FileText, FileSpreadsheet, Printer, Calendar,
} from "lucide-react";

// Helpers de fecha (America/Guatemala local del navegador)
function toIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function hoyIso() { return toIsoDate(new Date()); }
function rangoSemana() {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // lunes=0
  const desde = new Date(d); desde.setDate(d.getDate() - dow);
  return [toIsoDate(desde), toIsoDate(d)];
}
function rangoMes() {
  const d = new Date();
  const desde = new Date(d.getFullYear(), d.getMonth(), 1);
  return [toIsoDate(desde), toIsoDate(d)];
}

export default function CuadreRepartidor() {
  const [desde, setDesde] = useState(hoyIso());
  const [hasta, setHasta] = useState(hoyIso());
  const [preset, setPreset] = useState("hoy"); // hoy | semana | mes | rango
  const [pilotoId, setPilotoId] = useState("all");
  const [repartidores, setRepartidores] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/domicilios/repartidores").then(({ data }) => setRepartidores(data || [])).catch(() => {});
  }, []);

  const setPresetFn = (p) => {
    setPreset(p);
    if (p === "hoy") { const h = hoyIso(); setDesde(h); setHasta(h); }
    else if (p === "semana") { const [d, h] = rangoSemana(); setDesde(d); setHasta(h); }
    else if (p === "mes") { const [d, h] = rangoMes(); setDesde(d); setHasta(h); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (pilotoId && pilotoId !== "all") params.piloto_id = pilotoId;
      const { data } = await api.get("/domicilios/cuadre", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [desde, hasta, pilotoId]);

  const actuar = async (order_id, pago_id, estado_cuadre) => {
    if (estado_cuadre === "rechazado" &&
        !window.confirm("Rechazar este pago lo ELIMINARÁ del pedido (desaparece también en App Domicilio). ¿Continuar?")) {
      return;
    }
    try {
      const { data } = await api.post("/domicilios/cuadre/pago", { order_id, pago_id, estado_cuadre });
      toast.success(data?.eliminado ? "Pago rechazado y eliminado" : `Pago marcado como ${estado_cuadre}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Exports
  const buildQuery = () => {
    const params = new URLSearchParams({ desde, hasta });
    if (pilotoId && pilotoId !== "all") params.append("piloto_id", pilotoId);
    return params.toString();
  };
  const descargar = async (fmt) => {
    try {
      const url = `/domicilios/cuadre.${fmt}?${buildQuery()}`;
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = new Blob([data], { type: fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `cuadre_${desde}_${hasta}.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="cuadre-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tight">Cuadre Repartidor</h1>
          <p className="text-sm text-zinc-500">Filtra por rango de fechas y repartidor. Los totales se calculan sobre el período seleccionado.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="btn-print"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          <Button variant="outline" onClick={() => descargar("pdf")} data-testid="btn-pdf"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => descargar("xlsx")} data-testid="btn-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {[
            { k: "hoy", l: "Hoy" },
            { k: "semana", l: "Semana" },
            { k: "mes", l: "Mes" },
            { k: "rango", l: "Rango" },
          ].map((p) => (
            <button
              key={p.k}
              onClick={() => setPresetFn(p.k)}
              className={`px-3 py-2 rounded text-sm font-bold uppercase tracking-wide ${preset === p.k ? "bg-black text-white" : "bg-zinc-100 text-zinc-700"}`}
              data-testid={`preset-${p.k}`}
            >
              {p.l}
            </button>
          ))}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPreset("rango"); }} className="w-40" data-testid="cuadre-desde" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPreset("rango"); }} className="w-40" data-testid="cuadre-hasta" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Repartidor</Label>
          <Select value={pilotoId} onValueChange={setPilotoId}>
            <SelectTrigger className="w-56" data-testid="cuadre-piloto"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {repartidores.map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="w-4 h-4 mr-1" /> Actualizar</Button>
      </Card>

      {data && (
        <>
          {/* Resumen superior Ronda 16 — 4 KPIs de ruta + 3 KPIs de cuadre.
              Se eliminó "Efectivo" del top; ahora aparece dentro de Totales por banco. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Total ruta" value={data.resumen.total_ruta || 0} bg="bg-zinc-900 text-white" testid="k-total-ruta" isCount />
            <MiniStat label="Total entregados" value={data.resumen.total_entregados || 0} bg="bg-emerald-600 text-white" testid="k-total-entregados" isCount />
            <MiniStat label="Pendientes de entrega" value={data.resumen.pendientes_entrega || 0} bg="bg-amber-500 text-white" testid="k-pendientes-entrega" isCount />
            <MiniStat label="Total cobrado" value={data.resumen.total_cobrado} bg="bg-black text-white" testid="k-total-cobrado" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Cuadrado" value={data.resumen.cuadrado} bg="bg-emerald-100 text-emerald-900" testid="k-cuadrado" />
            <MiniStat label="Pendiente de cuadre" value={data.resumen.pendiente} bg="bg-amber-100 text-amber-900" testid="k-pendiente-cuadre" />
            <MiniStat label="Rechazado" value={data.resumen.rechazado} bg="bg-red-100 text-red-900" testid="k-rechazado" />
          </div>

          {/* Tarjetas dinámicas por banco */}
          {data.bancos && data.bancos.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Totales por banco</div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {data.bancos.map((b, i) => (
                  <MiniStat
                    key={i}
                    label={b.nombre}
                    value={b.total}
                    bg={b.total > 0 ? "bg-blue-100 text-blue-900" : "bg-zinc-50 text-zinc-500"}
                    testid={`k-banco-${b.nombre}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Ruta de Ciudad — Ronda 20 (tabla separada, misma fuente que Total Ruta) */}
          <Card className="p-4">
            <div className="text-xl font-semibold mb-3 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Ruta de Ciudad ({(data.pedidos_ruta || []).length})
            </div>
            {(data.pedidos_ruta || []).length === 0 ? (
              <div className="text-center text-zinc-500 py-6 text-sm">Sin pedidos de ciudad asignados a ruta en el rango seleccionado.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="tabla-ruta-ciudad">
                  <thead className="bg-zinc-50 text-zinc-600 text-left text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="px-2 py-2 border">Fecha</th>
                      <th className="px-2 py-2 border">N° Pedido</th>
                      <th className="px-2 py-2 border">Cliente</th>
                      <th className="px-2 py-2 border">Dirección</th>
                      <th className="px-2 py-2 border">Teléfonos</th>
                      <th className="px-2 py-2 border">Productos</th>
                      <th className="px-2 py-2 border text-right">Total</th>
                      <th className="px-2 py-2 border text-right">Anticipo</th>
                      <th className="px-2 py-2 border text-right">Cobrado</th>
                      <th className="px-2 py-2 border text-right">A cobrar</th>
                      <th className="px-2 py-2 border">Estado</th>
                      <th className="px-2 py-2 border">Repartidor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {(data.pedidos_ruta || []).map((p) => (
                      <tr key={p.id} className="align-top hover:bg-zinc-50" data-testid={`ruta-row-${p.numero}`}>
                        <td className="px-2 py-2 border whitespace-nowrap">{p.fecha}</td>
                        <td className="px-2 py-2 border">
                          <Link to={`/pedidos/${p.id}`} className="font-black text-[#002FA7] hover:underline inline-flex items-center gap-1">
                            {p.numero} <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                        <td className="px-2 py-2 border">{p.cliente}</td>
                        <td className="px-2 py-2 border max-w-[220px] whitespace-normal">{p.direccion}{p.zona ? ` · ${p.zona}` : ""}</td>
                        <td className="px-2 py-2 border whitespace-nowrap text-xs">{p.telefonos || "—"}</td>
                        <td className="px-2 py-2 border max-w-[260px] whitespace-normal text-xs">{p.productos}</td>
                        <td className="px-2 py-2 border text-right tabular-nums font-bold">{formatMoney(p.total)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums text-blue-700">{formatMoney(p.anticipo)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums text-emerald-700">{formatMoney(p.cobrado)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums font-bold">{formatMoney(p.a_cobrar)}</td>
                        <td className="px-2 py-2 border"><EstadoEntregaBadge estado={p.estado_entrega} /></td>
                        <td className="px-2 py-2 border">{p.piloto_nombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Tabla completa: Pedidos con pagos del día */}
          <Card className="p-4">
            <div className="text-xl font-semibold mb-3 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Pedidos con pagos del período ({data.pedidos.length})
            </div>
            {data.pedidos.length === 0 ? (
              <div className="text-center text-zinc-500 py-8 text-sm">Sin pagos registrados en el rango seleccionado.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-zinc-50 text-zinc-600 text-left text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="px-2 py-2 border">Fecha</th>
                      <th className="px-2 py-2 border">N° Pedido</th>
                      <th className="px-2 py-2 border">Vendedor</th>
                      <th className="px-2 py-2 border">Cliente</th>
                      <th className="px-2 py-2 border">Dirección</th>
                      <th className="px-2 py-2 border">Teléfonos</th>
                      <th className="px-2 py-2 border">Productos</th>
                      <th className="px-2 py-2 border text-center">Cant.</th>
                      <th className="px-2 py-2 border text-right">Subtotal</th>
                      <th className="px-2 py-2 border text-right">Envío</th>
                      <th className="px-2 py-2 border text-right">Total</th>
                      <th className="px-2 py-2 border text-right">A cobrar</th>
                      <th className="px-2 py-2 border text-right">Cobrado</th>
                      <th className="px-2 py-2 border text-right">Saldo</th>
                      <th className="px-2 py-2 border">Estado</th>
                      <th className="px-2 py-2 border">Hora entrega</th>
                      <th className="px-2 py-2 border">Obs.</th>
                      <th className="px-2 py-2 border">Repartidor</th>
                      <th className="px-2 py-2 border">Pagos</th>
                      <th className="px-2 py-2 border">Cuadre</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {data.pedidos.map((ped) => (
                      <tr key={ped.id} className="align-top hover:bg-zinc-50">
                        <td className="px-2 py-2 border whitespace-nowrap">{ped.fecha}</td>
                        <td className="px-2 py-2 border">
                          <Link to={`/pedidos/${ped.id}`} className="font-black text-[#002FA7] hover:underline inline-flex items-center gap-1">
                            {ped.numero} <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                        <td className="px-2 py-2 border">{ped.vendedor}</td>
                        <td className="px-2 py-2 border">{ped.cliente}</td>
                        <td className="px-2 py-2 border max-w-[220px] whitespace-normal">{ped.direccion}{ped.zona ? ` · ${ped.zona}` : ""}</td>
                        <td className="px-2 py-2 border whitespace-nowrap text-xs" data-testid={`cuadre-telefonos-${ped.numero}`}>{ped.telefonos || [ped.telefono, ped.telefono2].filter(Boolean).join(" / ") || "—"}</td>
                        <td className="px-2 py-2 border max-w-[260px] whitespace-normal text-xs">{ped.productos}</td>
                        <td className="px-2 py-2 border text-center">{ped.cantidad}</td>
                        <td className="px-2 py-2 border text-right tabular-nums">{formatMoney(ped.subtotal)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums text-blue-700">{formatMoney(ped.envio_precio)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums font-bold">{formatMoney(ped.total)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums">{formatMoney(ped.total_a_cobrar)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums text-emerald-700 font-bold">{formatMoney(ped.cobrado)}</td>
                        <td className="px-2 py-2 border text-right tabular-nums">{formatMoney(ped.saldo)}</td>
                        <td className="px-2 py-2 border">
                          <EstadoEntregaBadge estado={ped.estado_entrega} entregado_hoy={ped.entregado_hoy} />
                        </td>
                        <td className="px-2 py-2 border whitespace-nowrap text-xs">{ped.hora_entrega ? new Date(ped.hora_entrega).toLocaleString("es-GT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "—"}</td>
                        <td className="px-2 py-2 border max-w-[180px] whitespace-normal text-xs">{ped.observaciones}</td>
                        <td className="px-2 py-2 border">{ped.piloto_nombre}</td>
                        {/* Pagos — solo lectura: método/banco y monto */}
                        <td className="px-2 py-2 border" data-testid={`cuadre-pagos-${ped.numero}`}>
                          <div className="space-y-0.5">
                            {(ped.pagos || []).length === 0 && <span className="text-zinc-300 text-xs">—</span>}
                            {(ped.pagos || []).map((p) => (
                              <div key={p.id} className="text-xs">
                                <span className="font-bold">{p.metodo_pago}{p.banco && p.metodo_pago.toLowerCase() !== "efectivo" ? ` · ${p.banco}` : ""}</span>
                                <span className="ml-1 tabular-nums font-black text-emerald-700">{formatMoney(p.monto)}</span>
                                {p.referencia && <span className="ml-1 text-[10px] text-zinc-500 font-mono">· {p.referencia}</span>}
                                {p.comprobante_foto && <a href={p.comprobante_foto} target="_blank" rel="noreferrer" className="ml-1 text-blue-700 text-[10px] underline">foto</a>}
                              </div>
                            ))}
                          </div>
                        </td>
                        {/* Cuadre — controles de aprobación por pago */}
                        <td className="px-2 py-2 border" data-testid={`cuadre-acciones-${ped.numero}`}>
                          <div className="space-y-1">
                            {(ped.pagos || []).length === 0 && <span className="text-zinc-300 text-xs">—</span>}
                            {(ped.pagos || []).map((p) => (
                              <div key={p.id} className="flex items-center gap-1 text-xs border rounded p-1 bg-white">
                                <EstadoCuadreBadge estado={p.estado_cuadre || "pendiente"} />
                                <button title="Cuadrar" onClick={() => actuar(ped.id, p.id, "cuadrado")} className="p-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200" data-testid={`cuadrar-${p.id}`}><CheckCircle2 className="w-3 h-3" /></button>
                                <button title="Rechazar" onClick={() => actuar(ped.id, p.id, "rechazado")} className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200" data-testid={`rechazar-${p.id}`}><XCircle className="w-3 h-3" /></button>
                                <button title="Pendiente" onClick={() => actuar(ped.id, p.id, "pendiente")} className="p-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200" data-testid={`pendiente-${p.id}`}><Clock className="w-3 h-3" /></button>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, bg, testid, isCount = false }) {
  return (
    <Card className={`p-3 rounded-md border-0 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-black opacity-80 truncate">{label}</div>
      <div className="text-lg font-black tabular-nums mt-0.5">{isCount ? (value || 0) : formatMoney(value || 0)}</div>
    </Card>
  );
}

function EstadoCuadreBadge({ estado }) {
  const map = {
    cuadrado: "bg-emerald-600 text-white",
    pendiente: "bg-amber-500 text-white",
    rechazado: "bg-red-600 text-white",
  };
  return <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest text-center ${map[estado] || map.pendiente}`}>{estado}</span>;
}

function EstadoEntregaBadge({ estado, entregado_hoy }) {
  const map = {
    entregado: "bg-blue-600 text-white",
    no_contesta: "bg-amber-500 text-white",
    reprogramado: "bg-teal-500 text-white",
    falta_producto: "bg-orange-500 text-white",
    danado: "bg-red-600 text-white",
    cancelado: "bg-zinc-600 text-white",
    parcial: "bg-amber-500 text-white",
  };
  const cls = map[estado] || "bg-zinc-300 text-zinc-800";
  return <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${cls}`}>{estado || "—"}</span>;
}
